import React from "react";
import Box from "@mui/material/Box";
import Modal from "@mui/material/Modal";
import CloseIcon from "@mui/icons-material/Close";
import TextField from "@mui/material/TextField";
import AutoAwesomeIcon from "@mui/icons-material/AutoAwesome";
import AddIcon from "@mui/icons-material/Add";
import ViewModuleIcon from "@mui/icons-material/ViewModule";
import ViewHeadlineIcon from "@mui/icons-material/ViewHeadline";
import DescriptionOutlinedIcon from "@mui/icons-material/DescriptionOutlined";
import NotesIcon from "@mui/icons-material/Notes";
import FolderOutlinedIcon from "@mui/icons-material/FolderOutlined";
import LinkIcon from "@mui/icons-material/Link";
import { toast } from "react-toastify";
import ContentSourcePicker, { formatSourceUrl } from "./ContentSourcePicker";
import { useReferenceSources } from "./useReferenceSources";
import { postRightworkAPI } from "../requests/utilities/requests";
import {
  API_generate_roleplay_with_ai,
  API_generate_form_with_ai,
  API_generate_contest_with_ai,
  API_generate_plan_task_with_ai,
} from "../../../constants";

// One AI generate/edit modal shared by asset editors (roleplay, form), varied
// only by copy + endpoint via the VARIANTS map. Mirrors AiQuizGeneratorModal,
// with three deliberate differences: the author prompt is REQUIRED (it drives
// the draft), reference sources are OPTIONAL (grounding, not raw material),
// and generation is one synchronous call whose draft lands back in the editor
// for review — never saved server-side.
//
// When props.currentAsset is set (the editor has content), the modal opens in
// EDIT mode: the prompt is a revision request applied to that asset (same
// endpoint, the variant's currentParam) and untouched fields come back
// verbatim. A mode switch lets the author start fresh instead.
//
// Variants with `showGrounding: false` (contest, task) hide the LMS
// reference-content picker entirely — those endpoints take prompt-only input
// (their company context, like metric catalogs and role names, is loaded
// server-side). props.requestParams merges extra POST params (e.g. the
// contest's store_id + contest_type) into the request.
const VARIANTS = {
  roleplay: {
    endpoint: API_generate_roleplay_with_ai,
    draftKey: "scenario_draft",
    currentParam: "current_scenario",
    titleGenerate: "Generate Practice Scenario with AI",
    titleEdit: "Edit Scenario with AI",
    subtitleGenerate:
      "Describe what to practice; add company content to ground the scenario",
    subtitleEdit: "Describe the change; everything else stays as it is",
    editPill: "Edit current scenario",
    promptLabelGenerate: "What should the employee practice? *",
    promptLabelEdit: "What should change? *",
    placeholderGenerate:
      'E.g. "A guest asks whether the pad thai has peanuts because their kid has a severe allergy" or "Practice our comp policy on a cold-food complaint"',
    placeholderEdit:
      'E.g. "Make the guest a man in his 60s" or "Have her also complain about the drink order, and add a criterion about staying calm"',
    helpGenerate:
      "The AI drafts the character, situation, opening line, and success criteria. You review and edit everything before saving.",
    helpEdit:
      "The AI applies your change and leaves the rest of the scenario untouched. You review everything before saving.",
    buttonGenerate: "Generate Scenario",
    groundingHelp:
      "With sources selected, the scenario uses your real menu items, policies, and procedures instead of generic ones.",
    errorGenerate: "Couldn't generate a scenario. Please try again.",
    errorEdit: "Couldn't edit the scenario. Please try again.",
  },
  form: {
    endpoint: API_generate_form_with_ai,
    draftKey: "form_draft",
    currentParam: "current_form",
    titleGenerate: "Generate Form with AI",
    titleEdit: "Edit Form with AI",
    subtitleGenerate:
      "Describe the form; add company content to ground it in your policies",
    subtitleEdit: "Describe the change; everything else stays as it is",
    editPill: "Edit current form",
    promptLabelGenerate: "What should the form capture? *",
    promptLabelEdit: "What should change? *",
    placeholderGenerate:
      'E.g. "A trainer rubric for evaluating a new server\'s first solo shift" or "A self-reflection for new hires to complete after their first week"',
    placeholderEdit:
      'E.g. "Make all the rating scales 1-10" or "Add a food safety section with two checkboxes"',
    helpGenerate:
      "The AI drafts the fields, scoring, and settings. You review and edit everything before saving.",
    helpEdit:
      "The AI applies your change and leaves the rest of the form untouched. You review everything before saving.",
    buttonGenerate: "Generate Form",
    groundingHelp:
      "With sources selected, the form uses your real menu items, policies, and procedures instead of generic ones.",
    errorGenerate: "Couldn't generate a form. Please try again.",
    errorEdit: "Couldn't edit the form. Please try again.",
  },
  // Engagement tab: same endpoint and draft shape as "form" (the server's
  // purpose=engagement prompt variant does the rest), survey wording.
  survey: {
    endpoint: API_generate_form_with_ai,
    draftKey: "form_draft",
    currentParam: "current_form",
    titleGenerate: "Generate Survey with AI",
    titleEdit: "Edit Survey with AI",
    subtitleGenerate:
      "Describe the survey; add company content to ground it in your policies",
    subtitleEdit: "Describe the change; everything else stays as it is",
    editPill: "Edit current survey",
    promptLabelGenerate: "What should the survey ask? *",
    promptLabelEdit: "What should change? *",
    placeholderGenerate:
      'E.g. "A quarterly engagement pulse for hourly staff" or "A guest feedback survey for the new brunch menu"',
    placeholderEdit:
      'E.g. "Make it shorter" or "Add a question about scheduling flexibility"',
    helpGenerate:
      "The AI drafts the questions and screens. You review and edit everything before saving.",
    helpEdit:
      "The AI applies your change and leaves the rest of the survey untouched. You review everything before saving.",
    buttonGenerate: "Generate Survey",
    groundingHelp:
      "With sources selected, the survey uses your real menu items, policies, and procedures instead of generic ones.",
    errorGenerate: "Couldn't generate a survey. Please try again.",
    errorEdit: "Couldn't edit the survey. Please try again.",
  },
  contest: {
    endpoint: API_generate_contest_with_ai,
    draftKey: "contest_draft",
    currentParam: "current_contest",
    showGrounding: false,
    titleGenerate: "Generate Contest with AI",
    titleEdit: "Edit Contest with AI",
    subtitleGenerate:
      "Describe the contest; the AI picks the metric, dates, and rules",
    subtitleEdit: "Describe the change; everything else stays as it is",
    editPill: "Edit current contest",
    promptLabelGenerate: "What's the contest? *",
    promptLabelEdit: "What should change? *",
    placeholderGenerate:
      'E.g. "Two-week dessert upsell contest for servers starting Monday, $100 for first place, at least 5 shifts to qualify"',
    placeholderEdit: 'E.g. "Make it a month long with three winners"',
    helpGenerate:
      "The AI fills in the contest details. You review and can change everything before creating it. Stores and participants stay yours to pick.",
    helpEdit:
      "The AI applies your change and leaves the rest of the contest untouched.",
    buttonGenerate: "Generate Contest",
    errorGenerate: "Couldn't generate a contest. Please try again.",
    errorEdit: "Couldn't edit the contest. Please try again.",
  },
  task: {
    endpoint: API_generate_plan_task_with_ai,
    draftKey: "plan_task_draft",
    currentParam: "current_task",
    showGrounding: false,
    titleGenerate: "Generate Task with AI",
    titleEdit: "Edit Task with AI",
    subtitleGenerate:
      "Describe the task; the AI drafts the checklist and schedule",
    subtitleEdit: "Describe the change; everything else stays as it is",
    editPill: "Edit current task",
    promptLabelGenerate: "What's the task? *",
    promptLabelEdit: "What should change? *",
    placeholderGenerate:
      'E.g. "Kitchen closing checklist every night from 9 to 11pm, with photo proof for the walk-in temp log"',
    placeholderEdit: 'E.g. "Add a step for restocking to-go supplies"',
    helpGenerate:
      "The AI drafts the checklist steps, schedule, and time window. You review and edit everything before saving.",
    helpEdit:
      "The AI applies your change and leaves the rest of the task untouched.",
    buttonGenerate: "Generate Task",
    errorGenerate: "Couldn't generate a task. Please try again.",
    errorEdit: "Couldn't edit the task. Please try again.",
  },
};

function AiAssetGeneratorModal(props) {
  const variant = VARIANTS[props.variant] || VARIANTS.roleplay;
  const showGrounding = variant.showGrounding !== false;

  const style = {
    position: "absolute",
    top: "50%",
    left: "50%",
    transform: "translate(-50%, -50%)",
    width: 700,
    maxHeight: "98vh",
    bgcolor: "background.paper",
    borderRadius: "16px",
    boxShadow: "0 25px 50px -12px rgba(0, 0, 0, 0.25)",
    overflowY: "auto",
    border: "none",
  };

  const [userPrompt, setUserPrompt] = React.useState("");
  const [selectedSections, setSelectedSections] = React.useState([]);
  const [selectedModules, setSelectedModules] = React.useState([]);
  // Grounding-less variants never fetch the reference-source lists (some of
  // their hosts don't even have a homeCompany prop to fetch with).
  const refs = useReferenceSources(props.homeCompany, props.open && showGrounding);
  const [pastedText, setPastedText] = React.useState("");
  const [pickerOpen, setPickerOpen] = React.useState(false);
  const [isGenerating, setIsGenerating] = React.useState(false);
  // "edit" revises props.currentAsset; "generate" designs from scratch.
  // Defaults to edit whenever the editor has content to edit.
  const [mode, setMode] = React.useState("generate");
  const canEdit = !!props.currentAsset;
  const isEdit = mode === "edit" && canEdit;

  const handleSectionToggle = (sectionUuid) => {
    setSelectedSections((prev) =>
      prev.includes(sectionUuid)
        ? prev.filter((uuid) => uuid !== sectionUuid)
        : [...prev, sectionUuid]
    );
  };

  const handleModuleToggle = (moduleUuid) => {
    setSelectedModules((prev) =>
      prev.includes(moduleUuid)
        ? prev.filter((uuid) => uuid !== moduleUuid)
        : [...prev, moduleUuid]
    );
  };

  const handleGenerate = async () => {
    if (!userPrompt.trim() || isGenerating) return;
    setIsGenerating(true);
    try {
      const res = await postRightworkAPI(variant.endpoint, {
        company_id: props.homeCompany,
        user_prompt: userPrompt.trim(),
        ...(showGrounding
          ? {
              learning_section_uuids: JSON.stringify(selectedSections),
              learning_module_uuids: JSON.stringify(selectedModules),
              file_upload_uuids: JSON.stringify(refs.expandedFileUuids),
              pasted_text: pastedText.trim(),
            }
          : {}),
        ...(props.requestParams || {}),
        ...(isEdit
          ? { [variant.currentParam]: JSON.stringify(props.currentAsset) }
          : {}),
      });
      const payload = res.data?.redux?.payload || {};
      (payload.warnings || []).forEach((w) => toast.warn(w, { autoClose: 6000 }));
      if (!payload[variant.draftKey]) {
        throw new Error("No draft returned");
      }
      props.onGenerated(payload[variant.draftKey]);
      props.handleClose();
    } catch (error) {
      toast.error(
        error?.response?.data?.system?.message ||
          (isEdit ? variant.errorEdit : variant.errorGenerate),
        { autoClose: 4000 }
      );
    } finally {
      setIsGenerating(false);
    }
  };

  React.useEffect(() => {
    if (props.open) {
      setUserPrompt("");
      setSelectedSections([]);
      setSelectedModules([]);
      refs.reset();
      setPastedText("");
      setIsGenerating(false);
      setMode(props.currentAsset ? "edit" : "generate");
    }
    setPickerOpen(false);
    // Run only on open/close; refs.reset is stable (useReferenceSources).
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [props.open]);

  const sectionsData = props.sectionsData || [];
  const modulesData = props.modulesData || [];

  const sectionByUuid = React.useMemo(() => {
    const m = new Map();
    sectionsData.forEach((s) => m.set(s.uuid, s));
    return m;
  }, [sectionsData]);
  const moduleByUuid = React.useMemo(() => {
    const m = new Map();
    modulesData.forEach((mm) => m.set(mm.uuid, mm));
    return m;
  }, [modulesData]);

  const selectedModuleObjects = selectedModules
    .map((u) => moduleByUuid.get(u))
    .filter(Boolean);
  const selectedSectionObjects = selectedSections
    .map((u) => sectionByUuid.get(u))
    .filter(Boolean);
  const pastedSelected = pastedText.trim().length > 0;
  const totalSelected =
    selectedModuleObjects.length +
    selectedSectionObjects.length +
    refs.selectedFolderObjects.length +
    refs.selectedFiles.length +
    (pastedSelected ? 1 : 0);
  const canGenerate = userPrompt.trim().length > 0 && !isGenerating;

  return (
    <>
      <Modal open={props.open} aria-labelledby="ai-asset-generator-modal">
        <Box sx={style}>
          <>
            {/* Header */}
            <div className="sticky top-0 bg-white border-b border-gray-200 px-6 py-4 mx-0 mt-0 mb-6 z-10">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 bg-violet-100 rounded-lg flex items-center justify-center">
                    <AutoAwesomeIcon className="text-violet-600" style={{ fontSize: "20px" }} />
                  </div>
                  <div>
                    <h3 className="text-2xl font-bold text-slate-900">
                      {isEdit ? variant.titleEdit : variant.titleGenerate}
                    </h3>
                    <p className="text-sm text-slate-600">
                      {isEdit ? variant.subtitleEdit : variant.subtitleGenerate}
                    </p>
                  </div>
                </div>
                <button
                  className="w-8 h-8 rounded-lg hover:bg-gray-100 flex items-center justify-center text-gray-500 hover:text-gray-700 transition-colors"
                  onClick={() => props.handleClose()}
                >
                  <CloseIcon />
                </button>
              </div>
            </div>

            <div className="px-6 space-y-6">
              {/* Mode switch — only when there's an asset to edit */}
              {canEdit && (
                <div className="inline-flex rounded-lg border border-slate-200 bg-slate-50 p-0.5">
                  <button
                    type="button"
                    onClick={() => setMode("edit")}
                    className={`px-3 py-1.5 rounded-md text-sm font-medium transition-colors ${
                      mode === "edit"
                        ? "bg-white text-violet-700 shadow-sm"
                        : "text-slate-600 hover:text-slate-800"
                    }`}
                  >
                    {variant.editPill}
                  </button>
                  <button
                    type="button"
                    onClick={() => setMode("generate")}
                    className={`px-3 py-1.5 rounded-md text-sm font-medium transition-colors ${
                      mode === "generate"
                        ? "bg-white text-violet-700 shadow-sm"
                        : "text-slate-600 hover:text-slate-800"
                    }`}
                  >
                    Start fresh
                  </button>
                </div>
              )}

              {/* What to build / what to change */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  {isEdit ? variant.promptLabelEdit : variant.promptLabelGenerate}
                </label>
                <TextField
                  placeholder={
                    isEdit ? variant.placeholderEdit : variant.placeholderGenerate
                  }
                  fullWidth
                  multiline
                  rows={3}
                  variant="outlined"
                  size="small"
                  value={userPrompt}
                  onChange={(e) => setUserPrompt(e.target.value)}
                  InputProps={{
                    sx: {
                      backgroundColor: "white",
                      borderRadius: "8px",
                      "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                        borderColor: "#6d28d9",
                      },
                    },
                  }}
                />
                <p className="text-xs text-gray-500 mt-1">
                  {isEdit ? variant.helpEdit : variant.helpGenerate}
                </p>
              </div>

              {/* Reference content — optional grounding (LMS variants only) */}
              {showGrounding && (
              <div className="space-y-2">
                <div className="flex items-center justify-between">
                  <label className="block text-sm font-medium text-gray-700">
                    Reference Content (Optional)
                  </label>
                  <span className="text-xs text-slate-500">
                    {totalSelected === 0
                      ? "None selected"
                      : `${totalSelected} selected`}
                  </span>
                </div>

                {totalSelected > 0 && (
                  <div className="flex flex-wrap gap-1.5">
                    {refs.selectedFolderObjects.map((f) => (
                      <span
                        key={`fld-${f.uuid}`}
                        className="inline-flex items-center gap-1.5 text-xs bg-amber-50 border border-amber-200 text-amber-900 rounded px-2 py-1"
                      >
                        <FolderOutlinedIcon style={{ fontSize: 13 }} className="text-amber-600" />
                        <span className="font-medium">{f.name}</span>
                        <span className="text-amber-700/70">
                          · {f.file_count} file{f.file_count === 1 ? "" : "s"}
                        </span>
                        <button
                          onClick={() => refs.toggleFolder(f.uuid)}
                          className="ml-0.5 text-amber-700 hover:text-amber-900"
                          aria-label={`Remove ${f.name}`}
                        >
                          <CloseIcon style={{ fontSize: 12 }} />
                        </button>
                      </span>
                    ))}
                    {selectedModuleObjects.map((m) => (
                      <span
                        key={`m-${m.uuid}`}
                        className="inline-flex items-center gap-1.5 text-xs bg-violet-50 border border-violet-200 text-violet-900 rounded px-2 py-1"
                      >
                        <ViewModuleIcon style={{ fontSize: 13 }} className="text-violet-600" />
                        <span className="font-medium">{m.name}</span>
                        <button
                          onClick={() => handleModuleToggle(m.uuid)}
                          className="ml-0.5 text-violet-700 hover:text-violet-900"
                          aria-label={`Remove ${m.name}`}
                        >
                          <CloseIcon style={{ fontSize: 12 }} />
                        </button>
                      </span>
                    ))}
                    {selectedSectionObjects.map((s) => (
                      <span
                        key={`s-${s.uuid}`}
                        className="inline-flex items-center gap-1.5 text-xs bg-white border border-slate-200 text-slate-700 rounded px-2 py-1"
                      >
                        <ViewHeadlineIcon style={{ fontSize: 13 }} className="text-slate-500" />
                        <span className="font-medium">{s.name}</span>
                        <button
                          onClick={() => handleSectionToggle(s.uuid)}
                          className="ml-0.5 text-slate-500 hover:text-slate-700"
                          aria-label={`Remove ${s.name}`}
                        >
                          <CloseIcon style={{ fontSize: 12 }} />
                        </button>
                      </span>
                    ))}
                    {refs.selectedFileObjects.map((f) => {
                      const url = formatSourceUrl(f.source_url);
                      const label = url || f.name || "Document";
                      return (
                        <span
                          key={`f-${f.uuid}`}
                          className="inline-flex items-center gap-1.5 text-xs bg-sky-50 border border-sky-200 text-sky-900 rounded px-2 py-1"
                        >
                          {url ? (
                            <LinkIcon style={{ fontSize: 13 }} className="text-sky-600" />
                          ) : (
                            <DescriptionOutlinedIcon style={{ fontSize: 13 }} className="text-sky-600" />
                          )}
                          <span className="font-medium">{label}</span>
                          <button
                            onClick={() => refs.toggleFile(f.uuid)}
                            className="ml-0.5 text-sky-700 hover:text-sky-900"
                            aria-label={`Remove ${label}`}
                          >
                            <CloseIcon style={{ fontSize: 12 }} />
                          </button>
                        </span>
                      );
                    })}
                    {pastedSelected && (
                      <span className="inline-flex items-center gap-1.5 text-xs bg-amber-50 border border-amber-200 text-amber-900 rounded px-2 py-1">
                        <NotesIcon style={{ fontSize: 13 }} className="text-amber-600" />
                        <span className="font-medium">Pasted text</span>
                        <button
                          onClick={() => setPastedText("")}
                          className="ml-0.5 text-amber-700 hover:text-amber-900"
                          aria-label="Remove pasted text"
                        >
                          <CloseIcon style={{ fontSize: 12 }} />
                        </button>
                      </span>
                    )}
                  </div>
                )}

                <button
                  type="button"
                  onClick={() => setPickerOpen(true)}
                  className="w-full px-3 py-2 border border-dashed border-slate-300 hover:border-violet-400 hover:bg-violet-50 text-violet-700 rounded-lg font-medium text-sm flex items-center justify-center gap-2 transition-colors"
                >
                  <AddIcon style={{ fontSize: 16 }} />
                  Add reference content
                </button>
                <p className="text-xs text-gray-500 mt-3">
                  {variant.groundingHelp}
                </p>
              </div>
              )}
            </div>

            {/* Footer */}
            <div className="sticky bottom-0 bg-white border-t border-gray-200 px-6 py-4 mx-0 mb-0 mt-8 z-10">
              <div className="flex items-center justify-end gap-3">
                <button
                  className="px-6 py-2.5 border border-gray-300 hover:bg-gray-50 text-gray-700 rounded-lg font-medium transition-colors"
                  onClick={() => props.handleClose()}
                  disabled={isGenerating}
                >
                  Cancel
                </button>
                <button
                  className={`px-6 py-2.5 rounded-lg font-medium transition-colors flex items-center gap-2 ${
                    canGenerate
                      ? "bg-violet-600 hover:bg-violet-700 text-white"
                      : "bg-gray-200 text-gray-400 cursor-not-allowed"
                  }`}
                  onClick={handleGenerate}
                  disabled={!canGenerate}
                >
                  <AutoAwesomeIcon style={{ fontSize: "18px" }} />
                  {isGenerating
                    ? isEdit
                      ? "Applying…"
                      : "Generating…"
                    : isEdit
                    ? "Apply Edit"
                    : variant.buttonGenerate}
                </button>
              </div>
            </div>
          </>
        </Box>
      </Modal>
      {/* Sibling, not child, of the outer Modal — nesting a second Modal inside
          MUI's Modal breaks its focus-trap utility. */}
      {showGrounding && (
      <ContentSourcePicker
        open={pickerOpen}
        onClose={() => setPickerOpen(false)}
        companyId={props.homeCompany}
        showPaste
        showFolders
        files={refs.availableFiles}
        folders={refs.availableFolders}
        modules={modulesData}
        sections={sectionsData}
        selectedFileUuids={refs.selectedFiles}
        selectedFolderUuids={refs.selectedFolders}
        selectedModuleUuids={selectedModules}
        selectedSectionUuids={selectedSections}
        pastedText={pastedText}
        onToggleFile={refs.toggleFile}
        onToggleFolder={refs.toggleFolder}
        onToggleModule={handleModuleToggle}
        onToggleSection={handleSectionToggle}
        onPastedTextChange={setPastedText}
        onRefreshFiles={refs.refresh}
      />
      )}
    </>
  );
}

export default AiAssetGeneratorModal;
